"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { BorderBeamPanel } from "@/components/ui/border-beam-panel";

type PortfolioCard = {
  group: "setbase" | "independent";
  beamColor: string;
  title: string;
  description: string;
  role?: string;
  scope?: string;
  outcome?: string;
  highlight?: string;
  tech: string[];
  image?: string;
  imageFit?: "cover" | "contain";
  actions?: { label: string; projectKey: string }[];
  logos?: { name: string; iconUrl: string }[];
  visualVariant?: "backend-demo";
};

export default function ProjectSection() {
  const { t } = useTranslation();
  const [activeProject, setActiveProject] = useState<string | null>(null);
  const [backendDemoState, setBackendDemoState] = useState<
    "idle" | "running" | "complete"
  >("idle");
  const [backendDemoStep, setBackendDemoStep] = useState<number>(0);
  const backendDemoIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  const backendFlowNodes = [
    {
      label: t("portfolio.projects.backend.visual.nodes.api", {
        defaultValue: "API Gateway",
      }),
      badge: t("portfolio.projects.backend.visual.nodes.apiBadge", {
        defaultValue: "REST / GraphQL",
      }),
      layer: t("portfolio.projects.backend.visual.nodes.apiLayer", {
        defaultValue: "Express + GraphQL",
      }),
    },
    {
      label: t("portfolio.projects.backend.visual.nodes.auth", {
        defaultValue: "Keycloak Auth",
      }),
      badge: t("portfolio.projects.backend.visual.nodes.authBadge", {
        defaultValue: "JWT validation",
      }),
      layer: t("portfolio.projects.backend.visual.nodes.authLayer", {
        defaultValue: "Scopes + roles",
      }),
    },
    {
      label: t("portfolio.projects.backend.visual.nodes.database", {
        defaultValue: "PostgreSQL",
      }),
      badge: t("portfolio.projects.backend.visual.nodes.databaseBadge", {
        defaultValue: "Transactional store",
      }),
      layer: t("portfolio.projects.backend.visual.nodes.databaseLayer", {
        defaultValue: "Prisma entities",
      }),
    },
    {
      label: t("portfolio.projects.backend.visual.nodes.cache", {
        defaultValue: "Redis Cache",
      }),
      badge: t("portfolio.projects.backend.visual.nodes.cacheBadge", {
        defaultValue: "Latency optimization",
      }),
      layer: t("portfolio.projects.backend.visual.nodes.cacheLayer", {
        defaultValue: "Session + lookups",
      }),
    },
    {
      label: t("portfolio.projects.backend.visual.nodes.realtime", {
        defaultValue: "MQTT Realtime",
      }),
      badge: t("portfolio.projects.backend.visual.nodes.realtimeBadge", {
        defaultValue: "Event fan-out",
      }),
      layer: t("portfolio.projects.backend.visual.nodes.realtimeLayer", {
        defaultValue: "Broker + subscriptions",
      }),
    },
  ];
  const backendTechPreview = [
    {
      name: "MQTT",
      value: "pub/sub events",
    },
    {
      name: "GraphQL",
      value: "schema queries/mutations",
    },
    {
      name: "Keycloak",
      value: "OIDC + RBAC",
    },
    {
      name: "PostgreSQL",
      value: "persistent data",
    },
  ];
  const backendDevices = [
    {
      icon: "📱",
      name: t("portfolio.projects.backend.visual.devices.phone", {
        defaultValue: "Phone App",
      }),
      value: t("portfolio.projects.backend.visual.devices.phoneRole", {
        defaultValue: "Receives reminders and sync updates.",
      }),
    },
    {
      icon: "💊",
      name: t("portfolio.projects.backend.visual.devices.pillbox", {
        defaultValue: "Pillbox",
      }),
      value: t("portfolio.projects.backend.visual.devices.pillboxRole", {
        defaultValue: "Publishes status events and adherence signals.",
      }),
    },
  ];
  const backendPacketPath = [10, 30, 50, 70, 90];
  const isRealtimeActive =
    backendDemoState !== "idle" &&
    backendDemoStep >= backendFlowNodes.length - 1;

  const backendDemoButtonText =
    backendDemoState === "running"
      ? t("portfolio.projects.backend.visual.buttonRunning", {
          defaultValue: "Running request demo...",
        })
      : backendDemoState === "complete"
        ? t("portfolio.projects.backend.visual.buttonReplay", {
            defaultValue: "Replay backend request demo",
          })
        : t("portfolio.projects.backend.visual.buttonRun", {
            defaultValue: "Run backend request demo",
          });

  const backendDemoStatus =
    backendDemoState === "running"
      ? t("portfolio.projects.backend.visual.status.running", {
          defaultValue: "Streaming",
        })
      : backendDemoState === "complete"
                        ? t("portfolio.projects.backend.visual.status.complete", {
            defaultValue: "Completed",
          })
        : t("portfolio.projects.backend.visual.status.idle", {
            defaultValue: "Ready",
          });

  const backendDemoMetrics = [
    {
      label: t("portfolio.projects.backend.visual.metrics.requests", {
        defaultValue: "Req/min",
      }),
      value:
        backendDemoState === "idle"
          ? "—"
          : t("portfolio.projects.backend.visual.metrics.values.requests", {
              defaultValue: "142 req/min",
            }),
    },
      {
        label: t("portfolio.projects.backend.visual.metrics.auth", {
          defaultValue: "Auth success",
        }),
      value:
        backendDemoState === "idle"
          ? "—"
          : t("portfolio.projects.backend.visual.metrics.values.auth", {
              defaultValue: "99.8%",
            }),
    },
    {
      label: t("portfolio.projects.backend.visual.metrics.cache", {
        defaultValue: "Cache hit rate",
      }),
      value:
        backendDemoState === "idle"
          ? "—"
          : t("portfolio.projects.backend.visual.metrics.values.cache", {
              defaultValue: "84.2%",
            }),
    },
    {
      label: t("portfolio.projects.backend.visual.metrics.latency", {
        defaultValue: "P95 latency",
      }),
      value:
        backendDemoState === "idle"
          ? "—"
          : t("portfolio.projects.backend.visual.metrics.values.latency", {
              defaultValue: "47ms",
            }),
    },
  ];

  const runBackendDemo = () => {
    if (backendDemoState === "running") return;

    if (backendDemoIntervalRef.current) {
      clearInterval(backendDemoIntervalRef.current);
      backendDemoIntervalRef.current = null;
    }

    setBackendDemoState("running");
    setBackendDemoStep(0);

    let nextStep = 0;
    backendDemoIntervalRef.current = setInterval(() => {
      nextStep += 1;
      setBackendDemoStep(nextStep);

      if (nextStep >= backendFlowNodes.length - 1) {
        setBackendDemoState("complete");
        if (backendDemoIntervalRef.current) {
          clearInterval(backendDemoIntervalRef.current);
          backendDemoIntervalRef.current = null;
        }
      }
    }, 900);
  };

  useEffect(() => {
    return () => {
      if (backendDemoIntervalRef.current) {
        clearInterval(backendDemoIntervalRef.current);
      }
    };
  }, []);

  const projects: PortfolioCard[] = [
    {
      group: "setbase",
      beamColor: "#0f766e",
      title: t("portfolio.projects.adherlo.title"),
      description: t("portfolio.projects.adherlo.description"),
      role: t("portfolio.projects.adherlo.role"),
      scope: t("portfolio.projects.adherlo.scope"),
      outcome: t("portfolio.projects.adherlo.outcome"),
      highlight: t("portfolio.projects.adherlo.highlight"),
      tech: ["Next.js", "Three.js", "TailwindCSS", "TypeScript"],
      image: "/images/Adherlo_Website.jpg",
      actions: [
        {
          label: t("common.visitWebsite", { defaultValue: "Visit Website" }),
          projectKey: "adherlo",
        },
      ],
    },
    {
      group: "setbase",
      beamColor: "#635bff",
      title: t("portfolio.projects.store.title", {
        defaultValue: "Adherlo Store — E-Commerce",
      }),
      description: t("portfolio.projects.store.description", {
        defaultValue:
          "Multilingual e-commerce storefront with local PostgreSQL catalog, Stripe checkout, and Dockerized Ubuntu VPS deployment.",
      }),
      role: t("portfolio.projects.store.role", {
        defaultValue: "Storefront and checkout implementation",
      }),
      scope: t("portfolio.projects.store.scope", {
        defaultValue: "Catalog, multilingual UI, payments, deployment",
      }),
      outcome: t("portfolio.projects.store.outcome", {
        defaultValue: "Operational e-commerce flow for product sales",
      }),
      highlight: t("portfolio.projects.store.highlight", {
        defaultValue: "Stripe checkout with PostgreSQL-backed catalog",
      }),
      tech: ["Next.js 15", "TailwindCSS", "PostgreSQL", "Stripe", "Docker"],
      image: "/images/Adherlo_Ecommerce_Website.jpg",
      actions: [
        {
          label: t("common.visitWebsite", { defaultValue: "Visit Website" }),
          projectKey: "adherlo-store",
        },
      ],
    },
    {
      group: "setbase",
      beamColor: "#2563eb",
      title: t("portfolio.projects.mobile.title", {
        defaultValue: "Adherlo Mobile (Android & iOS)",
      }),
      description: t("portfolio.projects.mobile.description", {
        defaultValue:
          "Cross-platform React Native apps with Bluetooth device connectivity, medication schedules, notifications, and synced data.",
      }),
      role: t("portfolio.projects.mobile.role"),
      scope: t("portfolio.projects.mobile.scope"),
      outcome: t("portfolio.projects.mobile.outcome"),
      highlight: t("portfolio.projects.mobile.highlight"),
      tech: ["React Native", "Node.js", "PostgreSQL", "Keycloak", "MQTT"],
      image: "/images/Adherlo_App_Mockup.jpg",
      imageFit: "contain",
      actions: [
        {
          label: "Google Play",
          projectKey: "adherlo-google-play",
        },
        {
          label: "App Store",
          projectKey: "adherlo-app-store",
        },
      ],
    },
    {
      group: "independent",
      beamColor: "#5b6550",
      title: t("portfolio.projects.limowo.title"),
      description: t("portfolio.projects.limowo.description"),
      role: t("portfolio.projects.limowo.role"),
      scope: t("portfolio.projects.limowo.scope"),
      outcome: t("portfolio.projects.limowo.outcome"),
      highlight: t("portfolio.projects.limowo.highlight"),
      image: "/images/limowo-marketplace-mockup.png",
      tech: [
        "Next.js 15",
        "NestJS",
        "GraphQL",
        "PostgreSQL",
        "Prisma",
        "Keycloak",
        "MCP",
        "Docker",
        "Nginx",
      ],
      actions: [
        {
          label: t("common.visitWebsite", { defaultValue: "Visit Website" }),
          projectKey: "limowo",
        },
      ],
    },
    {
      group: "independent",
      beamColor: "#7c3aed",
      title: t("portfolio.projects.lilerno.title"),
      description: t("portfolio.projects.lilerno.description"),
      role: t("portfolio.projects.lilerno.role"),
      scope: t("portfolio.projects.lilerno.scope"),
      outcome: t("portfolio.projects.lilerno.outcome"),
      highlight: t("portfolio.projects.lilerno.highlight"),
      image: "/images/lilerno-learning-platform-mockup.png",
      tech: [
        "Next.js 16",
        "React 19",
        "Prisma",
        "PostgreSQL",
        "NextAuth",
        "Docker",
        "FSRS",
        "OpenRouter",
      ],
      actions: [
        {
          label: t("common.visitWebsite", { defaultValue: "Visit Website" }),
          projectKey: "lilerno",
        },
      ],
    },
    {
      group: "setbase",
      beamColor: "#ea580c",
      title: t("portfolio.projects.backend.title", {
        defaultValue: "Adherlo Backend",
      }),
      description: t("portfolio.projects.backend.description", {
        defaultValue:
          "Dockerized backend with CI/CD, GraphQL + REST APIs, PostgreSQL tuning, MQTT realtime, and secure Keycloak authentication.",
      }),
      role: t("portfolio.projects.backend.role"),
      scope: t("portfolio.projects.backend.scope"),
      outcome: t("portfolio.projects.backend.outcome"),
      highlight: t("portfolio.projects.backend.highlight"),
      tech: [
        "Node.js",
        "GraphQL",
        "PostgreSQL",
        "Keycloak",
        "MQTT",
        "Redis",
        "Docker",
      ],
      logos: [
        {
          name: "Node.js",
          iconUrl: "https://cdn.simpleicons.org/nodedotjs/5FA04E",
        },
        {
          name: "PostgreSQL",
          iconUrl: "https://cdn.simpleicons.org/postgresql/316192",
        },
        {
          name: "Keycloak",
          iconUrl: "https://cdn.simpleicons.org/keycloak/2C2255",
        },
        {
          name: "Docker",
          iconUrl: "https://cdn.simpleicons.org/docker/2496ED",
        },
        { name: "Redis", iconUrl: "https://cdn.simpleicons.org/redis/DC382D" },
      ],
    },
  ];
  const proofLabels = {
    role: t("portfolio.proof.role", { defaultValue: "Role" }),
    scope: t("portfolio.proof.scope", { defaultValue: "Scope" }),
    outcome: t("portfolio.proof.outcome", { defaultValue: "Outcome" }),
    highlight: t("portfolio.proof.highlight", {
      defaultValue: "Technical highlight",
    }),
  };
  const projectGroups = [
    {
      id: "setbase",
      eyebrow: t("portfolio.groups.setbase.eyebrow"),
      title: t("portfolio.groups.setbase.title"),
      subtitle: t("portfolio.groups.setbase.subtitle"),
    },
    {
      id: "independent",
      eyebrow: t("portfolio.groups.independent.eyebrow"),
      title: t("portfolio.groups.independent.title"),
      subtitle: t("portfolio.groups.independent.subtitle"),
    },
  ] as const;

  return (
    <section id="portfolio" className="space-y-5">
      <div className="rounded-xl border border-black/20 bg-white p-6 dark:border-white/25 dark:bg-neutral-950">
        <p className="font-display mb-2 text-sm uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400">
          Selected Work
        </p>
        <h2 className="font-display text-3xl tracking-[-0.04em] md:text-5xl">
          {t("portfolio.title")}
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-neutral-600 md:text-base dark:text-neutral-300">
          {t("portfolio.subtitle")}
        </p>
      </div>

      <div className="space-y-10">
        {projectGroups.map((group) => (
          <section key={group.id} className="space-y-5">
            <div className="border-l-2 border-black/20 pl-4 dark:border-white/25">
              <p className="font-display text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400">
                {group.eyebrow}
              </p>
              <h3 className="font-display mt-1 text-2xl tracking-[-0.04em] md:text-3xl">
                {group.title}
              </h3>
              <p className="mt-2 max-w-2xl text-sm text-neutral-600 dark:text-neutral-300">
                {group.subtitle}
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {projects
                .filter((project) => project.group === group.id)
                .map((project, index) => (
              <motion.article
            key={project.title}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: index * 0.04 }}
              className="group relative overflow-hidden rounded-xl border border-black/20 bg-white p-4 md:p-5 dark:border-white/25 dark:bg-neutral-950"
            onPointerEnter={() => setActiveProject(project.title)}
            onPointerLeave={() => setActiveProject(null)}
            onFocusCapture={() => setActiveProject(project.title)}
            onBlurCapture={(event) => {
              const nextTarget = event.relatedTarget;
              if (
                !(nextTarget instanceof Node) ||
                !event.currentTarget.contains(nextTarget)
              ) {
                setActiveProject(null);
              }
            }}
          >
            <div className="relative mb-3 aspect-[16/10] overflow-hidden rounded-lg border border-black/20 dark:border-white/25">
              {project.visualVariant === "backend-demo" ? (
                <div className="grid h-full w-full gap-3 rounded-md border border-slate-200 bg-white/85 p-4 text-slate-900 shadow-sm dark:border-slate-700/60 dark:bg-slate-950/80 dark:text-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-display text-xs uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">
                        {t("portfolio.projects.backend.visual.title", {
                          defaultValue: "Request pipeline",
                        })}
                      </p>
                      <p className="mt-1 text-sm font-semibold">
                        Adherlo API & Device Pipeline
                      </p>
                    </div>
                    <span className="rounded-sm border border-slate-300/70 px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] text-slate-600 dark:border-slate-500/50 dark:text-slate-300">
                      {t("portfolio.projects.backend.visual.metricsLabel", {
                        defaultValue: "Simulated",
                      })}
                    </span>
                  </div>

                  <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                    {t("portfolio.projects.backend.visual.subtitle", {
                      defaultValue:
                        "Request enters APIs, passes authentication, checks cache and DB, then publishes to MQTT subscriptions.",
                    })}
                  </p>

                  <div>
                    <p className="mb-1 text-[10px] uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                      {t("portfolio.projects.backend.visual.nodes.api", {
                        defaultValue: "Request pipeline",
                      })}
                    </p>
                    <div className="relative">
                      <div className="absolute left-0 right-0 top-8 h-0.5 rounded-full bg-slate-300/60 dark:bg-slate-700" />
                      <motion.div
                        className="absolute top-6.5 h-2.5 w-2.5 rounded-full bg-slate-900 shadow-[0_0_10px_rgba(15,23,42,0.35)] dark:bg-slate-100 dark:shadow-[0_0_10px_rgba(240,246,255,0.35)]"
                        style={{
                          left: `${backendPacketPath[Math.max(Math.min(backendDemoStep, backendPacketPath.length - 1), 0)]}%`,
                        }}
                        animate={{
                          opacity: backendDemoState === "idle" ? 0.35 : 1,
                          scale: backendDemoState === "running" ? [1, 1.1, 1] : 1,
                        }}
                        transition={{
                          duration: backendDemoState === "running" ? 1 : 0.2,
                          repeat:
                            backendDemoState === "running" ? Number.POSITIVE_INFINITY : 0,
                          repeatType: "mirror",
                        }}
                      />
                      <div className="relative z-10 grid grid-cols-5 gap-2">
                        {backendFlowNodes.map((node, index) => {
                          const isActive =
                            backendDemoState !== "idle" && backendDemoStep >= index;
                          const isCurrent =
                            backendDemoState !== "idle" &&
                            backendDemoStep === index;

                          return (
                            <div
                              key={node.label}
                              className="flex flex-col items-center gap-1 text-center"
                            >
                              <motion.div
                                className={`flex h-8 w-8 items-center justify-center rounded-full border text-[10px] font-semibold ${
                                  isActive
                                    ? "border-slate-900/80 bg-slate-900/10 text-slate-900 dark:border-slate-100/80 dark:bg-slate-100/10 dark:text-slate-100"
                                    : "border-slate-300 bg-slate-100 text-slate-500 dark:border-slate-700 dark:bg-slate-900/30 dark:text-slate-400"
                                }`}
                                animate={{
                                  scale: isCurrent ? 1.08 : 1,
                                }}
                              >
                                {index + 1}
                              </motion.div>
                              <p className="text-[9px] uppercase tracking-[0.06em] text-slate-600 dark:text-slate-300">
                                {node.label}
                              </p>
                              <p className="text-[8px] text-slate-500 dark:text-slate-400">
                                {node.badge}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-[1fr_1.3fr]">
                    <div className="rounded border border-slate-200 p-2 dark:border-slate-700/60">
                      <p className="text-[10px] uppercase tracking-[0.08em] text-slate-500 dark:text-slate-300">
                        {t("portfolio.projects.backend.visual.topology", {
                          defaultValue: "MQTT topology",
                        })}
                      </p>
                      <p className="mt-2 text-sm font-semibold">
                        {t("portfolio.projects.backend.visual.mqttLabel", {
                          defaultValue: "Event Hub",
                        })}
                      </p>
                      <p className="mt-1 text-[10px] text-slate-600 dark:text-slate-300">
                        {t("portfolio.projects.backend.visual.mqttFlow", {
                          defaultValue: "Publishes to subscribed Phone + Pillbox",
                        })}
                      </p>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {backendDevices.map((device) => (
                        <motion.div
                          key={device.name}
                          className={`rounded border px-2 py-2 text-left ${
                            isRealtimeActive
                              ? "border-slate-900/30 bg-slate-900/5 dark:border-slate-200/30 dark:bg-slate-100/10"
                              : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40"
                          }`}
                          animate={{
                            scale: isRealtimeActive ? [1, 1.02, 1] : 1,
                          }}
                          transition={{
                            duration: 1,
                            repeat: isRealtimeActive ? Infinity : 0,
                            repeatType: "mirror",
                          }}
                        >
                          <p className="text-xs font-semibold">
                            <span className="mr-1 inline-block">
                              {device.icon}
                            </span>
                            {device.name}
                          </p>
                          <p className="mt-1 text-[10px] text-slate-600 dark:text-slate-300">
                            {device.value}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={runBackendDemo}
                    disabled={backendDemoState === "running"}
                    className="mt-1 inline-flex w-fit items-center justify-center rounded-md border border-slate-300 px-3 py-2 text-xs uppercase tracking-[0.06em] transition hover:bg-slate-900 hover:text-white hover:border-slate-900 dark:border-slate-500/60 dark:hover:bg-slate-100 dark:hover:text-slate-900 dark:hover:border-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {backendDemoButtonText}
                  </button>

                  <p className="text-[10px] uppercase tracking-[0.08em] text-slate-500 dark:text-slate-300">
                    {t("portfolio.projects.backend.visual.statusLabel", {
                      defaultValue: "Demo status",
                    })}
                    : {backendDemoStatus}
                  </p>

                  <div>
                    <p className="text-[10px] uppercase tracking-[0.08em] text-slate-500 dark:text-slate-300">
                      {t("portfolio.projects.backend.visual.metricsLabel", {
                        defaultValue: "Simulated metrics",
                      })}
                    </p>
                    <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {backendDemoMetrics.map((metric) => (
                        <div
                          key={metric.label}
                          className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900/40"
                        >
                          <p className="text-[10px] uppercase tracking-[0.06em] text-slate-500 dark:text-slate-300">
                            {metric.label}
                          </p>
                          <p className="mt-0.5 text-sm font-semibold">
                            {metric.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-[0.08em] text-slate-500 dark:text-slate-300">
                      {t("portfolio.projects.backend.visual.technologyHeader", {
                        defaultValue: "Technology preview",
                      })}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {backendTechPreview.map((item) => (
                        <span
                          key={item.name}
                          className="rounded-full border border-slate-300 px-2.5 py-1 text-[10px] uppercase tracking-[0.06em] text-slate-600 dark:border-slate-500/80 dark:text-slate-300"
                        >
                          {item.name} — {item.value}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : project.image ? (
                <Image
                  src={project.image}
                  alt={project.title}
                  fill
                  className={`transition duration-500 group-hover:scale-105 ${
                    project.imageFit === "contain"
                      ? "object-contain bg-neutral-100 p-2 dark:bg-neutral-900"
                      : "object-cover"
                  }`}
                  unoptimized={project.image.startsWith("https://")}
                  sizes="(max-width: 639px) 100vw, (max-width: 1279px) 50vw, 33vw"
                />
              ) : (
                <div className="flex h-full flex-wrap items-center justify-center gap-2 bg-gradient-to-br from-slate-950 to-slate-800 p-4">
                  {project.logos?.length ? (
                    project.logos.map((logo) => (
                      <div
                        key={logo.name}
                        className="flex items-center gap-2 border border-black/20 bg-white px-3 py-1 text-xs text-black"
                      >
                        <Image
                          src={logo.iconUrl}
                          alt={logo.name}
                          width={14}
                          height={14}
                          unoptimized
                        />
                        <span>{logo.name}</span>
                      </div>
                    ))
                  ) : (
                    <span className="font-display max-w-xs text-center text-xl uppercase tracking-[0.08em] text-white">
                      {project.title}
                    </span>
                  )}
                </div>
              )}
            </div>

            <h3 className="font-display text-lg tracking-[-0.02em] md:text-xl">
              {project.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
              {project.description}
            </p>

            <dl className="mt-4 grid gap-2 border-t border-black/15 pt-4 text-xs dark:border-white/20">
              {(
                [
                  ["role", project.role],
                  ["scope", project.scope],
                  ["outcome", project.outcome],
                  ["highlight", project.highlight],
                ] as const
              ).map(([key, value]) =>
                value ? (
                  <div key={key} className="grid gap-1 sm:grid-cols-[7.5rem_1fr]">
                    <dt className="font-display uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
                      {proofLabels[key]}
                    </dt>
                    <dd className="leading-relaxed text-neutral-800 dark:text-neutral-200">
                      {value}
                    </dd>
                  </div>
                ) : null,
              )}
            </dl>

            <div className="mt-4 flex flex-wrap gap-2">
              {project.tech.map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-black/20 bg-transparent px-2.5 py-1 text-xs uppercase tracking-[0.06em] dark:border-white/25"
                >
                  {item}
                </span>
              ))}
            </div>

            {project.actions && (
              <div className="mt-4 flex flex-wrap gap-2">
                {project.actions.map((action) => (
                  <form
                    key={action.projectKey}
                    action={`/visit/${action.projectKey}`}
                    method="post"
                    target="_blank"
                  >
                    <button
                      type="submit"
                      className="font-display inline-flex cursor-pointer items-center gap-1 rounded-lg border border-black/20 px-3 py-2 text-xs uppercase tracking-[0.06em] transition hover:border-green-600 hover:text-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/30 dark:border-white/25 dark:hover:border-green-500 dark:hover:text-green-400"
                    >
                      {action.label}
                      <ArrowUpRight size={14} />
                    </button>
                  </form>
                ))}
              </div>
            )}
            <BorderBeamPanel
              aria-hidden="true"
              active={activeProject === project.title}
              color={project.beamColor}
              thickness={2}
              idleSpeed={10}
              radius={12}
              className="pointer-events-none absolute inset-0 z-10 border-0 bg-transparent dark:bg-transparent"
            >
              <span />
            </BorderBeamPanel>
          </motion.article>
                ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}
